import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetUserUtils } from '~/data/user';

import ToastService from '~/services/toast.service';

import { MissingPermissionsTooltip } from '~/utils/componentUtils';
import { LOG, log } from '~/utils/logging';

import { Icon, Reveal } from '@vestigas/ui/atoms';
import { Button, CopyToClipboard, Spinner } from '@vestigas/ui/molecules';

import { BasicFormConfirmAbort } from './BasicFormConfirmAbort';
import { BasicModal } from './BasicModal';

const StickyDialogFooter = ({ children }) => (
  <div
    className="sticky-dialog-footer z-10 flex items-center justify-between"
    style={{ width: `calc(100% - 48px)` }}
  >
    {children}
  </div>
);

const BasicForm = ({
  children,
  deleteButtonTitle = 'Löschen',
  deletingForm,
  disableSubmitButton,
  formAbort,
  formDelete,
  formSuccess,
  fullWidth,
  id,
  isLoading = false,
  isOpen,
  missingPermissionsToDelete,
  missingPermissionsToSubmit,
  noDeleteForm,
  opacity,
  open,
  submitButtonTitle = 'Speichern',
  submittingForm,
  testId,
  title,
  unsavedChanges = [],
  warningMessage,
}) => {
  const { t } = useTranslation('common');

  const [deleteFormOpen, setDeleteFormOpen] = useState(false);
  const [isAbortConfirmOpen, setIsAbortConfirmOpen] = useState(false);
  const { UserUtils } = useGetUserUtils();

  const handleCloseModal = (event) => {
    event.stopPropagation();

    if (unsavedChanges?.length) {
      setIsAbortConfirmOpen(true);
      return;
    }

    formAbort();
  };

  const handleCloseAbortForm = () => {
    setIsAbortConfirmOpen(false);
  };

  const handleConfirmAbort = (event) => {
    event.stopPropagation();
    event.preventDefault();

    log.productAnalyticsEvent('Submit abort form', LOG.FEATURE.FORM);

    formAbort();
    handleCloseAbortForm();
  };

  const handleCancelAbort = () => {
    log.productAnalyticsEvent('Abort abort form', LOG.FEATURE.FORM);
    handleCloseAbortForm();
  };

  const handleDeleteFormSuccess = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDeleteFormOpen(false);
    formDelete(event);
  };

  const handleDeleteFormAbort = () => {
    setDeleteFormOpen(false);
  };

  const renderTitle = () => (
    <div>
      <div className="flex items-center gap-2">
        {title}
        {id && UserUtils.isVestigasAccount() ? (
          <CopyToClipboard
            icon={
              <Icon
                className="text-tiga-surface-darker"
                metaphor="copy"
                size="sm"
              />
            }
            tooltipText={t(($) => $.copyToClipboard.id.tooltip)}
            value={id}
            onSuccess={() => {
              ToastService.info(t(($) => $.copyToClipboard.id.success));
            }}
          />
        ) : null}
      </div>
      <Reveal show={unsavedChanges?.length > 0}>
        <div className="text-warningBase text-sm font-normal">
          * Ungespeicherte Änderungen: {unsavedChanges.join(', ')}
        </div>
      </Reveal>
    </div>
  );

  const renderSubmitButton = () => {
    const isButtonDisabled =
      submittingForm || disableSubmitButton || missingPermissionsToSubmit;
    const button = (
      <Button
        className={
          isButtonDisabled ? 'disabled-primary-button' : 'primary-button'
        }
        color="primary"
        data-testid={`${testId}_submit_button`}
        disabled={isButtonDisabled}
        type="submit"
        variant="contained"
      >
        {submittingForm ? (
          <Spinner title={`${submitButtonTitle}...`} white />
        ) : (
          submitButtonTitle
        )}
      </Button>
    );

    if (missingPermissionsToSubmit) {
      return <MissingPermissionsTooltip>{button}</MissingPermissionsTooltip>;
    }

    return button;
  };

  const renderDeleteButton = () => {
    const button = (
      <Button
        color="secondary"
        disabled={deletingForm || missingPermissionsToDelete}
        variant="outlined"
        onClick={(event) => {
          noDeleteForm
            ? handleDeleteFormSuccess(event)
            : setDeleteFormOpen(true);
        }}
      >
        {deletingForm ? (
          <Spinner title={`${deleteButtonTitle}...`} />
        ) : (
          deleteButtonTitle
        )}
      </Button>
    );

    if (missingPermissionsToDelete) {
      return <MissingPermissionsTooltip>{button}</MissingPermissionsTooltip>;
    }

    return button;
  };

  const renderDeleteForm = () => (
    <BasicForm
      formAbort={handleDeleteFormAbort}
      formSuccess={handleDeleteFormSuccess}
      open={deleteFormOpen}
      submitButtonTitle="Löschen"
      title={`${title} löschen`}
    >
      <p className="max-w-[50ch] text-sm">
        Willst du mit dem Löschen fortfahren?
      </p>
      {warningMessage ? (
        <p className="mt-4 max-w-[50ch] text-sm">{warningMessage}</p>
      ) : null}
    </BasicForm>
  );

  return (
    <BasicModal
      closeModal={handleCloseModal}
      fullWidth={fullWidth}
      opacity={opacity}
      open={isOpen || open} // Transitional solution to make sure it does not break when somebody accidentally uses open instead of isOpen
      title={renderTitle()}
    >
      {isLoading ? (
        <div className="absolute inset-0 z-10 animate-overlay rounded-md bg-white/70">
          <Spinner title="Lade Daten..." />
        </div>
      ) : null}
      <form data-testid={testId} onSubmit={formSuccess}>
        <div className="min-w-80 pb-20">
          {' '}
          {/* Min width to prevent that footer buttons don't fit the whole row */}
          {children}
        </div>
        <StickyDialogFooter>
          {formDelete ? (
            <>
              {renderDeleteButton()}
              {renderDeleteForm()}
            </>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <Button variant="text" onClick={handleCloseModal}>
              Abbrechen
            </Button>
            <BasicFormConfirmAbort
              isOpen={isAbortConfirmOpen}
              onCancel={handleCancelAbort}
              onConfirm={handleConfirmAbort}
            />
            {renderSubmitButton()}
          </div>
        </StickyDialogFooter>
      </form>
    </BasicModal>
  );
};

export default BasicForm;
